/**
 * Every SWR key / endpoint path in one place, so cache invalidation can key off
 * a prefix:
 *   globalMutate((k) => typeof k === "string" && k.startsWith(QueryKeys.PRODUCT))
 *
 * Paths are relative to the proxy base (see BaseUrl); the gateway prefixes
 * `partner_user/` for partner-scoped resources and hits `v1/` directly for
 * auth/user/data/info/cashier_user (mirrors v1 pages/api/request.js routing).
 */
export const QueryKeys = {
	// Partner / account
	// `info` (not partner_user/info) — the gateway serves this one at v1/info.
	INFO: "info",
	SETTING: "partner_user/setting",
	SETTING_CURRENCY: "partner_user/setting/currency",
	UPDATE_SETTING: "partner_user/update_setting",
	PACKAGE: "partner_user/package",
	EXTEND_PACKAGE: "partner_user/extend_package",
	CHANGE_PACKAGE: "partner_user/change_package",
	BANK: "partner_user/bank",
	DISBURSE_BANK: "partner_user/disburse/bank",

	// Auth / user
	// v1 routed this through the partner_user prefix, not the auth one.
	CHANGE_PASSWORD: "partner_user/change-password",
	USER_DOCS_STATUS: "user/docs/status",
	USER_DOCS_ACCEPT: "user/docs/accept",
	/** `user/{id}/support-access` — grants support temporary sign-in. */
	SUPPORT_ACCESS: "user",

	// Store / staff
	STORE: "partner_user/store",
	STORE_PAGINATE: "partner_user/store/paginate",
	STORE_CATEGORY: "partner_user/category_store",
	CASHIER: "partner_user/cashier",
	CASHIER_RESET_PIN: "partner_user/cashier/reset-pin",

	// Catalog
	CATEGORY: "partner_user/category",
	ORDER_CATEGORY: "partner_user/order/category",
	PRODUCT: "partner_user/product",
	PRODUCT_IMPORT: "partner_user/product/import_product",
	PRODUCT_EXPORT: "partner_user/product/export",
	PRODUCT_SINGLE: "partner_user/product/create/single-product",
	VARIANT: "partner_user/variant",
	INGREDIENT: "partner_user/ingredient",
	SUPPLIER: "partner_user/supplier",
	SUPPLIER_PRODUCT_HISTORY: "partner_user/supplier/product-history",

	// Inventory
	STOCK_IN: "partner_user/inventory/stock_in",
	STOCK_OUT: "partner_user/inventory/stock_out",
	STOCK_TRANSFER: "partner_user/inventory/stock_transfer",
	STOCK_ADJUSTMENT: "partner_user/inventory/stock_adjustment",
	STOCK_CARD: "partner_user/inventory/stock_card",
	RESET_STOCK_CARD: "partner_user/inventory/reset_stock_card",
	RESET_STOCK_LOG: "partner_user/inventory/reset-stock-log",

	// Customers
	CUSTOMER: "partner_user/customer",
	CUSTOMER_IMPORT: "partner_user/customer/import",
	CUSTOMER_PURCHASE_REPORT: "partner_user/customer/purchase_report",
	CUSTOMER_DEBT_PAYMENT_REPORT: "partner_user/customer/debt_payment_report",
	CUSTOMER_DEBT_TRANSACTION: "partner_user/customer/debt_transaction",
	CUSTOMER_DEBT_REPORT: "partner_user/customer/debt_report",

	// Orders / reports
	ORDER_HISTORY: "partner_user/order/history",
	ORDER_RESET: "partner_user/order/reset",
	REPORT_SUMMARY: "partner_user/report/summary",
	REPORT_ORDER: "partner_user/report/order",
	REPORT_ORDER_EXPORT: "partner_user/report/order/export",
	REPORT_ORDER_PER_DAY: "partner_user/report/order_per_day",
	REPORT_ORDER_PER_PRODUCT: "partner_user/report/order_per_product",
	REPORT_ORDER_PER_STORE: "partner_user/report/order_per_store",
	REPORT_ORDER_PER_CUSTOMER: "partner_user/report/order_per_customer",
	REPORT_ORDER_PER_PAYMENT_METHOD:
		"partner_user/report/order_per_payment_method",
	REPORT_ORDER_DEBIT: "partner_user/report/order_debit",
	REPORT_EXPENSES: "partner_user/report/expenses",

	// Cards
	CARD: "partner_user/card",
	TOPUP_BATCH: "partner_user/topup/batch",
	TOPUP_HISTORY: "partner_user/topup/history",

	// Cazhbox
	CAZHBOX: "partner_user/cazhbox",
	CAZHBOX_PAGINATE: "partner_user/cazhbox/paginate",
	CAZHBOX_HISTORY: "partner_user/cazhbox/history",
	CAZHBOX_TRANSFER: "partner_user/cazhbox/transfer",
	CAZHBOX_WITHDRAW: "partner_user/cazhbox/withdraw",
	CAZHBOX_TOPUP: "cashier_user/cazhbox/topup",
	CAZHBOX_TOPUP_HISTORY: "cashier_user/cazhbox/topup/history",
	PARTNER_PAYMENT: "cashier_user/setting/partner-payment",

	// Loyalty / promo
	LOYALTY: "partner_user/loyalty",
	LOYALTY_REWARD: "partner_user/loyalty/reward",
	REWARD: "partner_user/reward",

	// Master data
	PROVINCE: "data/province",
	CITY: "data/city",
	COUNTRIES: "data/countries",
	UPLOAD: "data/upload",
} as const;

export type QueryKey = (typeof QueryKeys)[keyof typeof QueryKeys];
